/**
 * Fit (the whole picture) or fill for each remote picture on the web call
 * stage (VRSLayout.tsx). The rules are the native call stage's
 * (VRSLayout.native.tsx, PR #134):
 *
 * - Every picture fills its part of the stage (cover) by default.
 * - In a call of two, the other person covers the whole stage. When the
 *   orientation of their video crosses the stage's (an upright picture on a
 *   wide stage, or the reverse), the whole picture shows by default (Robert,
 *   2026-10-06: "we don't zoom all the way in").
 * - The fit button on a picture changes that person to the other choice. The
 *   tap holds until the default changes: when the stage or their camera
 *   turns and the default changes, the new default shows.
 *
 * The choice is for this browser tab and this call only: it is not stored and
 * not sent to anyone. Another room loads a new page, so it starts empty.
 *
 * Pure helpers here.
 */
/**
 * Whether the orientation of a video crosses the orientation of its place on
 * the stage. A square video, or a shape not known yet, does not cross. A
 * square place counts as upright, as a square screen does on native.
 *
 * @param {number|undefined} aspect - The width of the video divided by its height.
 * @param {ISlotSize|undefined} slot - The size of the place on the stage.
 * @returns {boolean}
 */
export function crossesSlot(aspect, slot) {
    if (!aspect || aspect === 1 || !Number.isFinite(aspect) || !slot || !(slot.width > 0) || !(slot.height > 0)) {
        return false;
    }
    return (aspect > 1) !== (slot.width > slot.height);
}
/**
 * Whether one remote picture shows whole (fit): the default, or the other
 * choice when the viewer tapped the fit button for this default.
 *
 * @param {FitChoice} choice - The viewer's taps.
 * @param {string} participantId - The person in the picture.
 * @param {boolean} fitByDefault - Whether the picture is fitted by default.
 * @returns {boolean}
 */
export function isPictureFitted(choice, participantId, fitByDefault) {
    return fitByDefault !== (choice[participantId] === fitByDefault);
}
/**
 * The taps after one more tap on the fit button of one picture. A second tap
 * goes back to the default.
 *
 * @param {FitChoice} choice - The viewer's taps.
 * @param {string} participantId - The person in the picture.
 * @param {boolean} fitByDefault - Whether the picture is fitted by default.
 * @returns {FitChoice}
 */
export function toggleFitChoice(choice, participantId, fitByDefault) {
    const next = { ...choice };
    if (next[participantId] === fitByDefault) {
        delete next[participantId];
    }
    else {
        next[participantId] = fitByDefault;
    }
    return next;
}
